'use client'

import { Loader2Icon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  creditPresets,
  type CreditPriceView,
  isSellableCredits,
  ROWS_PER_CREDIT,
} from '@/features/settings/billing/credits/lib/credits'
import { stashCheckoutBaseline } from '@/features/settings/billing/credits/use-credit-checkout-return'
import { useCreditFormat } from '@/features/settings/billing/credits/use-credit-format'
import { useNumberFormat, useNumberInput } from '@/hooks/use-number-format'
import { creditCheckoutUrls } from '@/lib/billing/routes'
import { cn } from '@/lib/utils'
import { useApiBilling, useCreateCreditCheckout } from '@/service/api-billing.hook'
import type { ErrorResponse } from '@/types/service'

/**
 * Buy any whole number of credits within the server's min..max. The presets are dollar amounts
 * that just fill the field; Stripe charges the per-credit price times the quantity.
 */
export default function BuyCreditsDialog({
  open,
  onOpenChange,
  price,
  grantedTotal,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  price: CreditPriceView
  grantedTotal: number
}) {
  const t = useTranslations('settings.billing.credits.buy')
  const fmt = useNumberFormat()
  const { cost } = useCreditFormat()
  const checkout = useCreateCreditCheckout()
  const { refetch: refetchBilling } = useApiBilling()
  const [creditsText, setCreditsText] = useState('')
  const [saveCard, setSaveCard] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const creditsInput = useNumberInput(creditsText, { integer: true })

  const presets = creditPresets(price)
  const credits = Number(creditsText)
  const valid = creditsText !== '' && isSellableCredits(price, credits)

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setCreditsText('')
      setSaveCard(false)
      setError(null)
    }
    onOpenChange(next)
  }

  const onContinue = async () => {
    if (!valid) return
    setError(null)
    try {
      // The baseline must be read now: a cached grantedTotal that predates a refill or another
      // purchase would let the return poll call this purchase done before its webhook lands.
      const { data: fresh } = await refetchBilling()
      const baseline = fresh?.grantedTotal ?? grantedTotal
      const checkoutUrl = await checkout.mutateAsync({
        credits: String(credits),
        saveCardForRefill: saveCard,
        ...creditCheckoutUrls(window.location.origin),
      })
      stashCheckoutBaseline(baseline)
      window.location.href = checkoutUrl
    } catch (e) {
      const failure = e as ErrorResponse
      // 403 = this origin isn't on the redirect allowlist; 5xx = Stripe isn't set up. Neither
      // message means anything to the user. A refusal (400) is written to be shown as-is.
      const generic = failure?.status === 403 || (failure?.status ?? 500) >= 500
      setError(generic || !failure?.message ? t('checkoutUnavailable') : failure.message)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[calc(100dvh-32px)] overflow-y-auto">
        <DialogTitle className="flex flex-col gap-2">
          {t('title')}
          <p className="text-foreground/60 text-sm font-normal tracking-tight">
            {t('description', { price: cost(price, 1) })}
          </p>
        </DialogTitle>

        <form
          className="mt-4 flex w-full flex-col gap-5"
          onSubmit={(event) => {
            event.preventDefault()
            void onContinue()
          }}
        >
          {presets.length > 0 && (
            <div role="radiogroup" aria-label={t('presets')} className="grid grid-cols-3 gap-2">
              {presets.map((preset) => {
                const active = valid && preset.credits === credits
                return (
                  <Button
                    key={preset.amount}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    variant={active ? 'default' : 'outline'}
                    className={cn(
                      'h-auto flex-col gap-0.5 px-3 py-2.5',
                      active && 'border border-transparent'
                    )}
                    onClick={() => setCreditsText(String(preset.credits))}
                  >
                    <span className="text-sm font-medium tabular-nums">
                      {cost(price, preset.credits)}
                    </span>
                    <span
                      className={cn(
                        'text-xs tabular-nums',
                        active ? 'text-emphasis-foreground/70' : 'text-foreground/50'
                      )}
                    >
                      {t('credits', { credits: fmt.number(preset.credits) })}
                    </span>
                  </Button>
                )
              })}
            </div>
          )}

          <div className="flex flex-col gap-1">
            <Label className="text-sm tracking-tight">{t('amount')}</Label>
            <Input
              {...creditsInput.bind(setCreditsText)}
              placeholder={t('amountPlaceholder', { min: fmt.number(price.minCredits) })}
              error={
                creditsText !== '' && !valid
                  ? t('amountRange', {
                      min: fmt.number(price.minCredits),
                      max: fmt.number(price.maxCredits),
                    })
                  : undefined
              }
            />
            {valid && (
              <span className="text-foreground/50 text-xs tabular-nums">
                {t('rows', { rows: fmt.number(credits * ROWS_PER_CREDIT) })}
              </span>
            )}
          </div>

          <label className="flex cursor-pointer items-start gap-2">
            <Checkbox
              className="mt-0.5"
              checked={saveCard}
              onCheckedChange={(checked) => setSaveCard(checked === true)}
            />
            <span className="flex flex-col">
              <span className="text-sm tracking-tight">{t('saveCard')}</span>
              <span className="text-foreground/50 text-xs tracking-tight">{t('saveCardHint')}</span>
            </span>
          </label>

          {error && <p className="text-destructive text-sm">{error}</p>}

          <Button type="submit" className="w-full" disabled={!valid || checkout.isPending}>
            {checkout.isPending ? (
              <Loader2Icon className="size-3.5 animate-spin" />
            ) : valid ? (
              t('pay', { total: cost(price, credits) })
            ) : (
              t('continue')
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  )
}
